<template>
  <!------------------------------------------
    This component displays the print version of the congressional state profiles.
  -------------------------------------------->
  <div id="congr-state-print-v">
    <div v-if="loading">
      <LoadingSpinner />
    </div>
    <Block v-else>
      <div class="print-instruction no-print">
        Use your browser's print functionality to print the page view below. Depending on what you see in the print preview, you may need to change the settings to enable "background graphics" (blue shading), disable "headers and footers" (the print date and other page info), and adjust margins. The layout has been developed and tested for printing from Google Chrome; results may vary on other browsers.
      </div>
      <div id="st-summary-area-print" v-if="profileData !== undefined && profileData !== null" :class="{ 'is-non-pr-territory': isTerritoryBesidesPuertoRico, 'is-pr-territory': selectedState === '72', 'is-mp-territory': selectedState === '69', 'is-vi-territory': selectedState === '78', 'is-as-territory': selectedState === '60', 'is-gu-territory': selectedState === '66' }">
        <!-- HEADER AREA -->
        <div class="grid-header">
          <div class="grid-header-left">
            <div class="main-title">
              <div class="title-1">Civil Legal needs & services in</div>
              <div class="title-2"><h2>{{ selectedStateName }}</h2></div>
            </div>
          </div>
          <div class="grid-header-right">
            <div class="grid-lsc-and-qr-info">
              <div class="summary-logo">
                <div><img id="summary-logo-img" :src="require(`@/assets/images/logo/lsc-logo-large.jpg`)"></div>
              </div>
              <div class="qr-info">
                Web version & more info
                <v-icon size="22" id="qr-right-arrow">
                  {{ icons.rightArrow }}
                </v-icon>
              </div>
            </div>
            <QrCode :url="qrUrl" :key="qrUrl" />
          </div>
        </div>

        <!-- ELIGIBILITY & POVERTY -->
        <div class="grid-pop grid-section grid-first-row">
          <ProfileDataElement title="LSC Eligible" icon="people" class="print-v">
            <ProfileDataPoint class="print-v" v-for="pnt in eligibilityPointsPrint" :key="pnt.name" :icon="pnt.icon" :dataPoint="String(pnt.dataPoint)" :text="pnt.text" :sideBySide="pnt.sideBySide" :afterText="pnt.afterText"/>
          </ProfileDataElement>
        </div>

        <!-- JUSTICE GAP STATS -->
        <div class="grid-gap grid-section grid-first-row">
          <ProfileDataElement title="Justice Gap Study" icon="scale" :shaded="false" :bordered="true" class="print-v">
            <ProfileDataPoint class="print-v" v-for="pnt in justiceGapPointsPrint" :key="pnt.name" :dataPoint="String(pnt.dataPoint)" :text="pnt.text" />
          </ProfileDataElement>
        </div>

        <!-- HOUSING STATS -->
        <div class="grid-housing grid-section grid-first-row">
          <ProfileDataElement title="Housing Issues" icon="house" class="print-v">
            <ProfileDataPoint class="print-v" v-for="pnt in housingPointsPrint" :key="pnt.name" :introText="pnt.introText" :sideBySide="pnt.sideBySide" :dataPoint="String(pnt.dataPoint)" :text="pnt.text" :comparisonDataPoint="pnt.comparisonDataPoint" :comparisonText="pnt.comparisonText" :afterText="pnt.afterText" :inlinePoint="pnt.inlinePoint" />
          </ProfileDataElement>
        </div>

        <!-- MAP -->
        <div class="grid-map grid-section grid-second-row">
          <StateProfilesPrintMap
            class="print-v" 
            :key="selectedState"
            :selectedState="selectedState"
            :interactive="false"
            mapboxStyle="mapbox://styles/lsc-odga/clah3hb8i000914m3b08kcyjd"
            :colorPerServArea="colorPerServArea"
            :officesPerGrantee="officesPerGrantee"
            :mapHeight="isTerritoryBesidesPuertoRico ? '30rem' : '18rem'"
          />
        </div>

        <!-- GRANTEES -->
        <div class="grid-grantees grid-section grid-second-row">
          <StateProfilesPrintGranteeList 
            class="print-v"
            :selectedState="selectedState"
            :granteesByType="granteesByType"
            :colorPerServArea="colorPerServArea"
            :officesPerGrantee="officesPerGrantee"
          />
        </div>

        <!-- PEOPLE SERVED -->
        <div class="grid-served grid-section grid-third-row">
          <ProfileDataElement :title="'Served in cases (' + garYear + ')'" icon="handshake" class="print-v">
            <ProfileDataPoint class="print-v" v-for="pnt in servedPointsPrint" :key="pnt.name" :icon="pnt.icon" :dataPoint="String(pnt.dataPoint)" :text="pnt.text" :sideBySide="pnt.sideBySide" />
          </ProfileDataElement>
        </div>

        <!-- CLOSED CASES -->
        <div class="grid-cases grid-section grid-third-row">
          <ProfileDataElement title="Top closed cases" icon="gavel" :shaded="false" class="print-v">
            <ProfileDataPoint class="print-v" v-for="pnt in casesPointsPrint" :key="pnt.name" :pointOnRight="true" :text="pnt.text" :italicText="pnt.italicText" :indentText="pnt.indentText" :dataPoint="pnt.dataPoint" :italicDataPoint="pnt.italicDataPoint" />
          </ProfileDataElement>
        </div>

        <!-- FUNDING -->
        <div class="grid-funding grid-section grid-third-row">
          <ProfileDataElement :title="'Funding in ' + fundingYear" icon="dollar" class="print-v">
            <ProfileDataPoint class="print-v" v-for="pnt in fundingPointsPrint" :key="pnt.name" :dataPoint="String(pnt.dataPoint)" :afterText="pnt.afterText" :text="pnt.text" :sideBySide="pnt.sideBySide" />
          </ProfileDataElement>
        </div>
      </div>
    </Block>
  </div>
</template>

<script>
import Block from '@/components/layout/Block'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import ProfileDataElement from '@/components/st_cd_snapshots/ProfileDataElement'
import ProfileDataPoint from '@/components/st_cd_snapshots/ProfileDataPoint'
import QrCode from '@/components/qr/QrCode'
import StateProfilesPrintMap from '@/views/advocacy/congress/state_profiles/StateProfilesPrintMap'
import StateProfilesPrintGranteeList from '@/views/advocacy/congress/state_profiles/StateProfilesPrintGranteeList'
import states from '@/js/stFips.js'
import { unique_colors } from '@/js/map_utils.js'
import { problemCodes, categoryRename, codesPerCategory } from '@/js/problem_codes.js'
import { mdiUndo } from '@mdi/js';
import { formatThousandsNoDecimal, percentToOneInX, formatPercentNoDecimalIfExists } from "@/js/number_formatters.js";

export default {
  name: 'StateProfilesPrint',

  components: { Block, QrCode, ProfileDataElement, ProfileDataPoint, LoadingSpinner, StateProfilesPrintMap, StateProfilesPrintGranteeList },

  props: {
    profileData: { type: Object, required: true },
    selectedState: { type: String, required: true },
    granteesByType: { type: Object, required: true },
    officesPerGrantee: { type: Object, required: true },
    isTerritory: { type: Boolean, required: true },
    isTerritoryBesidesPuertoRico: { type: Boolean, required: true }
  },

  data () {
    return {
      icons: {
        rightArrow: mdiUndo
      },
      loading: true,
      qrUrl: '',
      garYear: null,
      fundingYear: null,
      eligibilityPointsPrint: [],
      justiceGapPointsPrint: [],
      housingPointsPrint: [],
      servedPointsPrint: [],
      casesPointsPrint: [],
      fundingPointsPrint: [],
      colors: unique_colors
    }
  },

  computed: {
    selectedStateName () { 
      if (this.selectedState !== '' && this.selectedState !== null) {
        return states.filter(x => x.fips === String(this.selectedState))[0].state 
      } else {
        return ''
      }
    },
    colorPerServArea () {
      let bfServAreas = this.granteesByType.bfGeneral.map(x => ({ area: x.serv_area, recipient_id: x.recipient_id }))
      let c = this.colors
      let colorMap = {}
      let colorIndex = 0

      return bfServAreas.map(function(x) {
      if (!colorMap[x.recipient_id]) {
        colorMap[x.recipient_id] = c[colorIndex % c.length]
        colorIndex++
      }
      return { area: x.area, color: colorMap[x.recipient_id] }
      })
    }
  },

  created () {
    this.parseProfileData()
  },
  
  methods: {
    async parseProfileData () {
      this.loading = true
      this.garYear = this.profileData.gar_year
      this.fundingYear = this.profileData.funding.funding_year
      this.loadQr()
      this.setEligibilityPoints(this.profileData)
      this.setJusticeGapPoints(this.profileData)
      this.setHousingPoints(this.profileData)
      this.setServedPoints(this.profileData)
      this.setFundingPoints(this.profileData)
      this.setCasesPoints(this.profileData)
      this.loading = false
    },

    loadQr () {
      /**
       * Display a QR code for this URL (updating qrUrl will trigger the component)
       */
      this.$nextTick(function() {
        this.qrUrl = `https://www.lsc.gov/node/4025?embed=true&state=${this.selectedState}&from=qr`
      })
      return
    },

    setEligibilityPoints (d) {
      /**
       * Takes the data points in the fetched JSON file and orders them for display based on the business logic below (i.e. display the most compelling data points).
       * 
       * The function builds arrays of objects, where each object is a data point for display, "name" is a unique name for use in the v-for :key (and for readability), and the other properties are what will get passed to the ProfileDataPoint component.
       * 
       * The print version will accommodate a limited number of data points in the grid section on the page, while the
       * web version may have more data points and the arrangement on the page may be different. 
       * With the data points in priority order, the array of data points for print layout will then get sliced to the number of allowed data points as defined in the maxPrintPoints variable below.  
       * 
       * @param {object}  d   Object from parsing the fetched JSON file from getProfileData()
       * @return  No return, but updates the reactive data.
       */

      let printPoints = []
      const maxPrintPoints = 5
      // SLOT 1 = always this
      printPoints.push(
        { name: 'eligible people', icon: "people", dataPoint: `${percentToOneInX(d.population.pct_eligible)}`, text: "people", sideBySide: true }
      )
      // SLOTS 2 & 3 = always except non-PR territories
      if (this.isTerritoryBesidesPuertoRico === false) {
        printPoints.push(
          { name: 'eligible children', icon: "child", dataPoint: `${percentToOneInX(d.population.pct_children_in_elig_hh)}`, text: "children", sideBySide: true }
        )

        printPoints.push(
          { name: 'eligible seniors', icon: "seniors", dataPoint: `${percentToOneInX(d.population.seniors_eligible)}`, text: "older Americans", sideBySide: true }
        )
      }
      // SLOT 4 = veteran if available and > 1 in 15 (6.7%)
      if (d.population.vet_pov_pct !== null && d.population.vet_pov_pct > 0.067) {
        printPoints.push(
          { name: 'vet eligible', icon: "star", dataPoint: `${percentToOneInX(d.population.vet_pov_pct)}`, text: "veterans",  sideBySide: true }
        )
      }
      // SLOT 5 = rural if available
      if (d.population.rural_eligible !== null) {
        printPoints.push(
          { name: 'rural eligible', icon: "rural", dataPoint: `${percentToOneInX(d.population.rural_eligible)}`, text: "rural residents", sideBySide: true }
        )
      }

      this.eligibilityPointsPrint = printPoints.slice(0, maxPrintPoints)
      return
    },

    setJusticeGapPoints (d) {
      /**
       * Same as setEligibilityPoints() but for the Justice Gap data section. See intro to setEligibilityPoints().
       */
      let printPoints = []
      const maxPrintPoints = 2
      let region = d.justice_gap.region ? d.justice_gap.region.toLowerCase() : ''

      // SLOT 1 = always this
      let stateApostropheS = this.selectedStateName.charAt(this.selectedStateName.length - 1) === 's' ? '' : 's'
      printPoints.push(
        { name: 'Justice Gap % of problems unhelped', dataPoint: `Only ${formatPercentNoDecimalIfExists(d.justice_gap.problems_pct_unhelped)}`, text: `of the civil legal problems facing ${this.selectedStateName}'${stateApostropheS} low-income residents receive enough legal help.` }
      )
      // SLOT 2 = always this
      printPoints.push(
        { name: 'Justice Gap % of issues to grantees unhelped', dataPoint: `Only ${formatPercentNoDecimalIfExists(d.justice_gap.grantees_pct_unhelped)}`, text: `of issues brought to LSC grantees in ${region}ern states receive enough help.` }
      )
      this.justiceGapPointsPrint = printPoints.slice(0, maxPrintPoints)
      return
    },

    setHousingPoints (d) {
      /**
       * Same as setEligibilityPoints() but for the Justice Gap data section. See intro to setEligibilityPoints().
       */

      let printPoints = []
      const maxPrintPoints = 2

      // BOTH SLOTS DEPEND ON DATA AVAILABILITY AND THRESHOLDS
      // First priority is if eviction filing representation rates are available and the difference between them is at least 40 percentage points
      if (d.housing.eviction_representation !== null && "tenant" in d.housing.eviction_representation && "landlord" in d.housing.eviction_representation && (d.housing.eviction_representation.landlord - d.housing.eviction_representation.tenant > 0.4 )) {
        printPoints.push(
          { name: 'Eviction filing representation rates', dataPoint: `${formatPercentNoDecimalIfExists(d.housing.eviction_representation.tenant)}`, text: "of tenants have representation in court, compared to", comparisonDataPoint: `${formatPercentNoDecimalIfExists(d.housing.eviction_representation.landlord)}`, afterText: "of landlords.", inlinePoint: true}
        )
      }

      // Then, if the highest eviction filings rate amongst counties is greater than 10%
      if (d.housing.max_county_eviction_filings_rate !== null && d.housing.max_county_eviction_filings_rate.rate > 0.1) {
        printPoints.push(
          { name: 'Max filings rate', introText: `The rate of eviction filings in ${this.selectedStateName} counties is as high as`, dataPoint: `${percentToOneInX(d.housing.max_county_eviction_filings_rate.rate)}`, text: 'renter households.', sideBySide: true }
        )
      }

      // Percent rental cost burdened
      if (d.housing.pct_rent_burdened !== null) {
        printPoints.push(
          { name: 'Rental cost burdened', dataPoint: `${formatPercentNoDecimalIfExists(d.housing.pct_rent_burdened)}`, text: 'of renter households pay more than 30% of income on rent.', sideBySide: true }
        )
      }

      // Work hours to afford 1 bedroom at FMR -- if above 65 hours
      if (d.housing.nlihc !== null && "hrs_wk_for_1br_fmr" in d.housing.nlihc && d.housing.nlihc.hrs_wk_for_1br_fmr !== null && d.housing.nlihc.hrs_wk_for_1br_fmr > 65) {
        printPoints.push(
          { name: 'NLIHC hours of work', dataPoint: `${String(d.housing.nlihc.hrs_wk_for_1br_fmr)} hours/week`, text: 'at minimum wage to afford a 1-bedroom unit at Fair Market Rent', sideBySide: false }
        )
      }

      // Units per ELI renter HH
      if (d.housing.nlihc !== null && "units_per_eli" in d.housing.nlihc && d.housing.nlihc.units_per_eli !== null && d.housing.nlihc.units_per_eli < 65) {
        printPoints.push(
          { name: 'NLIHC units per ELI', dataPoint: `Only ${String(d.housing.nlihc.units_per_eli)}`,text: "affordable units are available for every 100 extremely low-income renter households.", inlinePoint: true}
        )
      }

      this.housingPointsPrint = printPoints.slice(0, maxPrintPoints)
      return
    },

    setServedPoints (d) {
      /**
       * Same as setEligibilityPoints() but for the Served data section. See intro to setEligibilityPoints().
       */

      let printPoints = []
      const maxPrintPoints = 4
      // SLOT 1 = always this
      printPoints.push(
        { name: 'People served', icon: "people", dataPoint: `${formatThousandsNoDecimal(d.served.people)}`, text: "people", sideBySide: true }
      )
      // SLOT 2 = always this
      printPoints.push(
        { name: 'Veterans served', icon: "star", dataPoint: `${formatThousandsNoDecimal(d.served.veterans)}`, text: "veterans", sideBySide: true }
      )
      // SLOT 3 = always this
      printPoints.push(
        { name: 'Seniors served', icon: "seniors", dataPoint: `${formatThousandsNoDecimal(d.served.seniors)}`, text: "older Americans", sideBySide: true }
      )
      // SLOT 4 = always this
      printPoints.push(
        { name: 'DV served', icon: "dv", dataPoint: `${formatThousandsNoDecimal(d.served.dv)}`, text: "domestic violence survivors", sideBySide: true }
      )
      this.servedPointsPrint = printPoints.slice(0, maxPrintPoints)
      return
    },

    setFundingPoints (d) {
      /**
       * Same as setEligibilityPoints() but for the Served data section. See intro to setEligibilityPoints().
       */

      let printPoints = []
      const maxPrintPoints = 4

      // First, BF General grants
      const bfVars = {'bf_general': 'to serve the eligible population', 'ag_worker': 'to serve agricultural workers', 'native_am': 'to serve Native Americans'}

      for (const x of Object.keys(bfVars)) {
        if (x in d.funding.basic_field && d.funding.basic_field[x] !== null && d.funding.basic_field[x] > 0) {
          printPoints.push(
            { name: `${x} funding`, dataPoint: `$${formatThousandsNoDecimal(d.funding.basic_field[x])}`, text: bfVars[x] }
          )
        }        
      }

      // Then special grants
      const sgVars = {'disaster': 'to respond to disasters', 'pbif': 'for pro bono innovation', 'tig': 'for technology innovation'}

      for (const x of Object.keys(sgVars)) {
        if (x in d.funding.special_grants && d.funding.special_grants[x] !== null && d.funding.special_grants[x] > 0) {
          printPoints.push(
            { name: `${x} funding`, dataPoint: `$${formatThousandsNoDecimal(d.funding.special_grants[x])}`, text: `since ${d.funding.special_grants.since_year}`, afterText: sgVars[x], sideBySide: true }
          )
        }        
      }

      this.fundingPointsPrint = printPoints.slice(0, maxPrintPoints)
      return
    },

    setCasesPoints (d) {
      /**
       * Same as setEligibilityPoints() but for the Cases Closed data section. See intro to setEligibilityPoints().
       */

      let printPoints = []
      const maxPrintPoints = 7
      let printPointsTally = 0

      // Convert categories and codes to new array of objects for sorting 
      const categories = this.keyValuesToSortedArrayOfObjects(d.closed_cases.categories)
      const codes = this.keyValuesToSortedArrayOfObjects(d.closed_cases.codes_over_10_pct)
    
      // Exclude the "other_" codes from each category
      const codesToExclude = ['09', '19', '29', '39', '49', '59', '69', '79', '89', '99']
      const miscCodes = ['90','91','92','93','94','95','96','97','98']

      // Treat problem codes under "Miscellaneous" as if they were problem categories
      for (let x of codes) {
        if (miscCodes.indexOf(x.key) > -1) {
          categories.push(x)
        }
      }

      categories.sort((a, b) => parseFloat(b.value) - parseFloat(a.value));

      // Until we reach the max number of points for display,
      // add each problem category (and its sub-codes) in order for the list of data points
      for (const c of categories) {

        // Stop if have enough for what can fit in the display
        if (printPointsTally > maxPrintPoints) { break }

        if (c.value > 0.02 && categoryRename[c.key] !== 'Miscellaneous' && categoryRename[c.key] !== 'Misc') {
          let name = miscCodes.indexOf(c.key) > -1 ? problemCodes.filter(x => x.code === parseInt(c.key))[0].shortName : categoryRename[c.key]

          printPoints.push(
            { name: `${c.key} cases`, text: name, dataPoint: `${formatPercentNoDecimalIfExists(c.value)}` }
          )
          printPointsTally += 1
        }

        // Show any codes within this category if > 10% of all cases closed
        if (miscCodes.indexOf(c.key) > -1 || c.key === 'misc') { continue }

        const codesForThisCategory = codes.filter(x => codesPerCategory[c.key].indexOf(parseInt(x.key)) > -1)

        if (codesForThisCategory.length > 0) {
          for (const co of codesForThisCategory) {
            if (co.value > 0.1 && codesToExclude.indexOf(co.key) === -1) {
              printPoints.push(
                { name: `${co.key} cases`, text: problemCodes.filter(x => x.code === parseInt(co.key))[0].shortName, dataPoint: `${formatPercentNoDecimalIfExists(co.value)}`, italicText: true, indentText: true, italicDataPoint: true }
              )
              printPointsTally += 1
            }
          }
        }
      }

      this.casesPointsPrint = printPoints.slice(0, maxPrintPoints)
      return
    },

    keyValuesToSortedArrayOfObjects(obj) {
      /**
       * Convert an object like { key1: value1, key2: value2 } to an array of objects 
       * like [{ key: key1, value: value1 }, { key: key2, value: value2 }]
       * @param {object}  obj Object like { key1: value1, key2: value2 }
       * @return {array}  Returns array of objects
       */
      return Object.entries(obj).map(([key, value]) => ({key, value})).sort((a, b) => b.value - a.value)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/scss/vars.scss';
.print-instruction {
  max-width: 60rem;
  color: #5a5a5a;
  font-style: italic;
  text-align: center;
  margin: 0 auto 5rem auto;
}
#st-summary-area-print {
  display: grid;
  grid-template-areas:
    'header header header header header header'
    'pop pop gap gap housing housing'
    'map map map grantees grantees grantees'
    'served served cases cases funding funding';
  gap: 1rem;
  font-size: 0.85rem;
  grid-auto-columns: 1fr;
  max-width: 43rem;
  min-width: 43rem;
  margin: 0 auto;

  .grid-header {
    grid-area: header;
    max-height: 5.5rem;
    min-height: 5.5rem;

    display: flex;
    justify-content: space-between;

    .grid-header-left {
      display: flex;
      flex-direction: column;

      .main-title {
        display: flex;
        flex-direction: column;
        justify-content: flex-start; 

        .title-1 {
          color: #404040;
          text-transform: uppercase;
        }
        .title-2 h2 {
          color: #828282;
          font-family: "Work Sans", sans-serif;
          margin-top: -0.7rem;
          font-size: 2.2rem;
          font-weight: 400;
        }
      }
    }

    .grid-header-right {
      display: flex;

      .grid-lsc-and-qr-info {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
      }

      #summary-logo-img {
        height: 55px;
        width: auto;
        display: block;
        margin-right: 1rem;
        padding: 0;
      }

      .qr-info {
        text-align: right;
        color: #5a5a5a;
        font-style: italic;
        margin-right: 0.45rem;
      }

      #qr-right-arrow {
        color: #5a5a5a;
        opacity: 0.5;
        transform: rotate(180deg);
      }
    }
  }
  .grid-pop {
    grid-area: pop;
  }
  .grid-gap {
    grid-area: gap;
  }
  .grid-housing {
    grid-area: housing;
  }
  .grid-map {
    grid-area: map;
  }
  .grid-grantees {
    grid-area: grantees;
  }
  .grid-served {
    grid-area: served;
  }
  .grid-cases {
    grid-area: cases;
  }
  .grid-funding {
    grid-area: funding;
  }
  .grid-first-row {
    max-height: 17rem;
    min-height: 17rem;
  }
  .grid-second-row {
    max-height: 18rem;
    min-height: 18rem;
  }
  .grid-third-row {
    max-height: 17rem;
    min-height: 17rem;
  }
  &.is-non-pr-territory {
    grid-template-areas:
      'header header header header header header'
      'pop pop cases cases served served'
      'funding funding cases cases served served'
      'map map map map map map'
      'grantees grantees grantees grantees grantees grantees';

    .grid-housing, .grid-gap {
      display: none;
    }
    .grid-first-row, .grid-second-row, .grid-third-row {
      max-height: none;
      min-height: none;
    }
    .grid-pop {
      max-height: 6rem;
      min-height: 6rem;
    }
    .grid-funding {
      max-height: 12rem;
      min-height: 12rem;
    }
    .grid-map {
      max-height: 24rem;
      min-height: 24rem;
    }
    .grid-grantees {
      max-height: 8rem;
      min-height: 8rem;

      #congr-state-print-grantees {
        justify-content: flex-start;
      }
    }
  }
  &.is-as-territory {
    .grid-pop {
      max-height: 8rem;
      min-height: 8rem;
    }
    .grid-funding {
      max-height: 10rem;
      min-height: 10rem;
    }
  }
  &.is-vi-territory {
    grid-template-areas:
      'header header header header header header'
      'pop pop cases cases served served'
      'funding funding cases cases served served'
      'map map map map grantees grantees';

    .grid-grantees {
      #congr-state-print-grantees {
        justify-content: center;
      }
    }
  }
  &.is-pr-territory {
    grid-template-areas:
      'header header header header header header'
      'pop pop cases cases served served'
      'map map map map map map'
      'grantees grantees other other funding funding';

    .grid-gap, .grid-housing {
      display: none;
    }
    #congr-state-print-grantees {
      justify-content: flex-start;
    }
      .grid-map {
      max-height: 16rem;
      min-height: 16rem;
    } 
  }
}

</style>